// GitHub App connect flow. Unlike the social OAuth libs, GitHub App uses an *installation*
// flow: we send the user to install the app on their repos, and GitHub redirects back to
// /auth/github with an `installation_id` and (because the App requests user authorization
// during installation) a `code`. `state` is round-tripped for CSRF validation on return.
export const GITHUB_AUTH_STATE_STORAGE_KEY = 'gh_auth_state' as const
export type GithubAuthState = { rnd: string; referrerPath: string; origin?: string }

/**
 * Origins the callback is allowed to hand off to. An allowlist, not a parsed-and-trusted
 * value: the relay below reads its destination out of `state`, which arrives through the URL
 * and is therefore attacker-controlled. Without this, `/auth/github?state={"origin":"…"}`
 * would be an open redirect that forwards a live OAuth `code` anywhere.
 */
const RELAY_ORIGINS: readonly string[] = [
  'https://app.getcassi.ai',
  'https://preview.getcassi.ai',
  'https://localhost:5173',
]

/**
 * GitHub ignores `redirect_uri` on the *installation* endpoint — it only honors it on the
 * authorize endpoint — so every environment lands on the App's FIRST registered callback URL.
 * One App serves three origins, so two of them always arrive on the wrong host.
 *
 * The first callback URL is production, and this forwards the callback on to whichever origin
 * actually started the flow. Returns true when it has navigated away and the caller should do
 * nothing further.
 *
 * Deliberately runs before any state validation: the relay is not a trust boundary, it only
 * moves the query string. The destination validates `state` against its own localStorage,
 * which is where the flow began and the only place the matching value exists.
 */
export const relayGithubCallbackIfNeeded = (rawState: string | null): boolean => {
  if (!rawState) return false
  let origin: unknown
  try {
    origin = (JSON.parse(rawState) as { origin?: unknown }).origin
  } catch {
    return false
  }
  if (typeof origin !== 'string' || origin === window.location.origin) return false
  if (!RELAY_ORIGINS.includes(origin)) return false

  window.location.replace(`${origin}/auth/github${window.location.search}`)
  return true
}

/** Where GitHub sends the user back. One App serves dev, preview and production, so this is
 *  derived from the current origin rather than configured per environment. */
export const githubRedirectUri = () => `${window.location.origin}/auth/github`

export const startGithubAppInstall = (opts: { slug: string; referrerPath: string }) => {
  const state: GithubAuthState = {
    referrerPath: opts.referrerPath,
    rnd: Math.random().toString().slice(3, 12),
    // Round-tripped so the callback can find its way home when GitHub lands it elsewhere.
    origin: window.location.origin,
  }
  localStorage.setItem(GITHUB_AUTH_STATE_STORAGE_KEY, JSON.stringify(state))

  const url = new URL(`https://github.com/apps/${opts.slug}/installations/new`)
  url.searchParams.set('state', JSON.stringify(state))
  // Required, not optional. With several callback URLs registered on the App, GitHub redirects
  // to the FIRST one unless the request names which to use — so production installs came back
  // to localhost. Must exactly match one of the App's registered callback URLs.
  url.searchParams.set('redirect_uri', githubRedirectUri())
  window.location.href = url.toString()
}
